// One offboarding case: what it records, what still blocks sign off, the 26
// steps, final pay, the letter, the certificate, the interview, the two
// signatures and the dated notes. Every rule is enforced in Postgres; this
// screen shows the refusal sentence when one comes back.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, FileText, Mail, Printer, ShieldCheck, UserMinus, X } from 'lucide-react'
import {
  useAddOffboardingNote, useApproveOffboarding, useCancelOffboarding, useGetCertificateOfService,
  useGetOffboardingCase, useGetOffboardingLetter, useRecordEmploymentState, useSendOffboardingLetter,
  useSignOffOffboarding, useUpdateOffboardingCase,
} from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import { Chip } from '../interviews/Chip'
import { Checklist } from './Checklist'
import { CaseEvidence } from './CaseEvidence'
import { FinalPayPanel } from './FinalPayPanel'
import { Ui19Panel } from './Ui19Panel'
import { ExitInterviewForm } from './ExitInterviewForm'
import { writeErrorMessage } from '../../utils/writeError'
import {
  EXIT_REASONS, EXIT_TYPES, NOTICE_POSITIONS, STATUS_LABEL, STATUS_TONE, exitTypeLabel, formatDay, formatWhen,
  isDismissal, payDueText, progress, type CaseDetail as Detail,
} from '../../utils/offboarding'

export function CaseDetail({ caseId, onChanged }: { caseId: string; onChanged: () => void }) {
  const q = useGetOffboardingCase<Detail>()
  const load = q.trigger
  const refresh = useCallback(() => {
    void load({ case_id: caseId }, { skipCache: true }).catch(() => undefined)
  }, [load, caseId])
  useEffect(() => { refresh() }, [refresh])
  const changed = useCallback(() => { refresh(); onChanged() }, [refresh, onChanged])

  const d = q.data
  if (q.error && !d) return <p className="p-6 text-sm text-rose-700 dark:text-rose-300">{q.error}</p>
  if (!d) return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the case…</p>
  if (d.summary.id !== caseId) return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the case…</p>

  const s = d.summary
  const editable = ['open', 'awaiting_signoff'].includes(s.status)
  const pay = payDueText(s)
  const prog = progress(s)

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-5">
        <header className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">{s.employee_name ?? 'Employee'}</h2>
            <span className="text-sm text-muted-foreground">{s.case_ref}</span>
            <Chip tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Chip>
            <Chip tone={pay.tone}>{pay.text}</Chip>
          </div>
          <p className="text-sm text-muted-foreground">
            {exitTypeLabel(s.exit_type)} · last working day {formatDay(s.last_working_day)}{s.dates_confirmed ? '' : ' (provisional)'} ·
            employment ends {formatDay(s.employment_end_date)} · {s.vehicle ?? 'no car'} · Driver Servant {s.driver_servant ?? 'not on file'}
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-[hsl(var(--brand))]" style={{ width: `${prog.pct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground">{prog.settled} of {prog.total} steps done or not applicable{s.items_overdue ? `, ${s.items_overdue} overdue` : ''}.</p>
          {s.status === 'cancelled' && s.cancelled_reason && <p className="text-sm text-muted-foreground">{s.cancelled_reason}</p>}
        </header>

        <SignOffBlock detail={d} onChanged={changed} />
        <DetailsForm detail={d} editable={editable && d.access.can_edit_case} onChanged={changed} />

        <CaseEvidence caseId={s.id} driverId={s.driver_id ?? null} version={`${prog.settled}-${s.status}`} onChanged={changed} />

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Checklist</h3>
          <Checklist caseId={s.id} items={d.items} editable={editable} onChanged={changed} />
        </section>

        <FinalPayPanel detail={d} onChanged={changed} />
        <LetterPanel detail={d} onChanged={changed} />
        <Ui19Panel detail={d} onChanged={changed} />
        <ExitInterviewForm detail={d} onChanged={changed} />
        <CertificatePanel caseId={s.id} />
        <EmploymentStatePanel detail={d} onChanged={changed} />
        <NotesPanel detail={d} onChanged={changed} />
        <CancelPanel detail={d} onChanged={changed} />
      </div>
    </div>
  )
}

function SignOffBlock({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const s = detail.summary
  const sign = useSignOffOffboarding<{ ok: boolean }>()
  const approve = useApproveOffboarding<{ ok: boolean }>()
  const [note, setNote] = useState('')

  async function run(what: string, fn: () => Promise<unknown>) {
    try { await fn(); toast.success(what); setNote(''); onChanged() } catch (err) { toast.error(writeErrorMessage(err)) }
  }

  const missing = detail.missing
  // The open steps are listed in the checklist below, so here they are a count;
  // what is spelled out is everything else the signature waits on.
  const caseGaps = missing.filter((m) => !m.startsWith('Step '))
  const openSteps = missing.length - caseGaps.length
  return (
    <section className={`space-y-2 rounded-lg border p-3 ${s.status === 'closed' ? 'border-emerald-300 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-950/30' : 'border-border'}`}>
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><ShieldCheck className="h-4 w-4 text-muted-foreground" /> Sign off</h3>
      {s.signed_off_by && <p className="text-xs text-foreground">Checklist signed by {s.signed_off_by} on {formatWhen(s.signed_off_at)}.{detail.case.signoff_note ? ` "${detail.case.signoff_note}"` : ''}</p>}
      {s.approved_by && <p className="text-xs text-foreground">Summary sign off by {s.approved_by} on {formatWhen(s.approved_at)}.{detail.case.approval_note ? ` "${detail.case.approval_note}"` : ''} The case is read only; a correction is a dated note.</p>}

      {['open', 'awaiting_signoff'].includes(s.status) && (
        missing.length > 0 ? (
          <div className="text-xs">
            <p className="font-medium text-foreground">Still missing before it can be signed off:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
              {caseGaps.map((m) => <li key={m}>{m}</li>)}
              {openSteps > 0 && <li>{openSteps} step{openSteps === 1 ? '' : 's'} of the checklist still open, listed below.</li>}
            </ul>
          </div>
        ) : <p className="text-xs text-foreground">Everything is recorded. The checklist is waiting for sign off.</p>
      )}

      {['open', 'awaiting_signoff'].includes(s.status) && detail.access.can_sign_off && (
        <div className="flex flex-wrap items-end gap-2">
          <Input className="h-8 max-w-sm text-xs" placeholder="A note with your signature (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button size="sm" variant="brand" disabled={sign.loading || missing.length > 0}
            onClick={() => void run('Checklist signed off', () => sign.trigger({ case_id: s.id, note }).result)}>
            {sign.loading && <Spinner className="mr-1 h-3 w-3" />} Sign off the checklist
          </Button>
        </div>
      )}
      {['open', 'awaiting_signoff'].includes(s.status) && !detail.access.can_sign_off && (
        <p className="text-[11px] text-muted-foreground">Xolile Ndika signs the checklist, or Yolisa or Luyanda when he is away.</p>
      )}

      {s.status === 'signed_off' && detail.access.can_approve && (
        <div className="flex flex-wrap items-end gap-2">
          <Input className="h-8 max-w-sm text-xs" placeholder="Note (needed to return it)" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button size="sm" variant="brand" disabled={approve.loading}
            onClick={() => void run('Summary sign off given', () => approve.trigger({ case_id: s.id, decision: 'approve', note }).result)}>
            Give the summary sign off
          </Button>
          <Button size="sm" variant="outline" disabled={approve.loading || !note.trim()}
            onClick={() => void run('Returned for another look', () => approve.trigger({ case_id: s.id, decision: 'return', note }).result)}>
            Return it
          </Button>
        </div>
      )}
      {s.status === 'signed_off' && !detail.access.can_approve && (
        <p className="text-[11px] text-muted-foreground">Waiting for Yolisa or Luyanda to give the summary sign off.</p>
      )}
    </section>
  )
}

type Form = Record<string, string>

function toForm(c: Detail['case']): Form {
  return {
    exit_type: c.exit_type ?? '', notice_received_or_given_date: c.notice_received_or_given_date ?? '',
    last_working_day: c.last_working_day ?? '', employment_end_date: c.employment_end_date ?? '',
    notice_position: c.notice_position ?? '', notice_position_note: c.notice_position_note ?? '',
    source_document_ref: c.source_document_ref ?? '', resignation_in_writing: c.resignation_in_writing ? 'yes' : '',
    hearing_date: c.hearing_date ?? '', outcome_document_ref: c.outcome_document_ref ?? '',
    written_reasons_given_on: c.written_reasons_given_on ?? '', exit_reason_primary: c.exit_reason_primary ?? '',
    exit_reason_detail: c.exit_reason_detail ?? '', rehire_eligible: c.rehire_eligible ?? '', rehire_reason: c.rehire_reason ?? '',
    certificate_include_reason: c.certificate_include_reason ? 'yes' : '',
  }
}

const BOOLEAN_FIELDS = new Set(['resignation_in_writing', 'certificate_include_reason'])

function DetailsForm({ detail, editable, onChanged }: { detail: Detail; editable: boolean; onChanged: () => void }) {
  const c = detail.case
  const save = useUpdateOffboardingCase<{ ok: boolean }>()
  const initial = useMemo(() => toForm(c), [c])
  const [f, setF] = useState<Form>(initial)
  useEffect(() => { setF(initial) }, [initial])
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }))
  const dirty = Object.keys(f).filter((k) => f[k] !== initial[k])
  const dismissal = isDismissal(f.exit_type)

  async function submit() {
    const patch: Record<string, unknown> = {}
    for (const k of dirty) patch[k] = BOOLEAN_FIELDS.has(k) ? f[k] === 'yes' : (f[k] === '' ? null : f[k])
    try {
      await save.trigger({ case_id: detail.summary.id, patch }).result
      toast.success('Case saved')
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  const dateField = (k: string, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="date" className="h-8 text-xs" value={f[k]} disabled={!editable} onChange={(e) => set(k, e.target.value)} />
    </div>
  )

  return (
    <section className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">The exit</h3>
        {!c.dates_confirmed && <Chip tone="amber" icon={<AlertTriangle className="h-3 w-3" />}>Dates provisional</Chip>}
        {c.dates_confirmed && c.dates_confirmed_by && <span className="text-[11px] text-muted-foreground">Dates confirmed by {c.dates_confirmed_by}</span>}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-3">
          <Label className="text-xs">Exit type</Label>
          <NativeSelect className="h-8 text-xs" value={f.exit_type} disabled={!editable} onChange={(e) => set('exit_type', e.target.value)}>
            <option value="">Not recorded</option>
            {EXIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </NativeSelect>
        </div>
        {dateField('notice_received_or_given_date', 'Notice received or given')}
        {dateField('last_working_day', 'Last working day')}
        {dateField('employment_end_date', 'Employment end date')}
        <div className="space-y-1">
          <Label className="text-xs">Notice</Label>
          <NativeSelect className="h-8 text-xs" value={f.notice_position} disabled={!editable} onChange={(e) => set('notice_position', e.target.value)}>
            <option value="">Not recorded</option>
            {NOTICE_POSITIONS.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
          </NativeSelect>
          <p className="text-[11px] text-muted-foreground">
            BCEA section 37 requires {c.notice_period_required_days ?? '?'} days here (service from {formatDay(c.service_start_date)}).
            {c.notice_position_by ? ` Recorded by ${c.notice_position_by}.` : ''}
          </p>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">Who agreed the notice position, and anything else about it</Label>
          <Input className="h-8 text-xs" value={f.notice_position_note} disabled={!editable} onChange={(e) => set('notice_position_note', e.target.value)} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">Where the source document is filed</Label>
          <Input className="h-8 text-xs" value={f.source_document_ref} disabled={!editable} onChange={(e) => set('source_document_ref', e.target.value)} />
        </div>
        {f.exit_type === 'resignation' && (
          <label className="flex items-center gap-2 self-end pb-1.5 text-xs text-foreground">
            <input type="checkbox" checked={f.resignation_in_writing === 'yes'} disabled={!editable}
              onChange={(e) => set('resignation_in_writing', e.target.checked ? 'yes' : '')} />
            The resignation is in writing
          </label>
        )}
      </div>

      {dismissal && (
        <div className="grid gap-3 rounded-md bg-muted/40 p-2 sm:grid-cols-3">
          <p className="text-[11px] text-muted-foreground sm:col-span-3">
            The case records that fair procedure happened. It does not decide the dismissal. Hearing chaired by {c.hearing_chair ?? 'the hearing chair on file'}.
          </p>
          {dateField('hearing_date', 'Disciplinary hearing')}
          {dateField('written_reasons_given_on', 'Written reasons given')}
          <div className="space-y-1">
            <Label className="text-xs">Where the signed outcome is filed</Label>
            <Input className="h-8 text-xs" value={f.outcome_document_ref} disabled={!editable} onChange={(e) => set('outcome_document_ref', e.target.value)} />
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label className="text-xs">Primary exit reason</Label>
          <NativeSelect className="h-8 text-xs" value={f.exit_reason_primary} disabled={!editable} onChange={(e) => set('exit_reason_primary', e.target.value)}>
            <option value="">Not recorded</option>
            {EXIT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </NativeSelect>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">In more words</Label>
          <Input className="h-8 text-xs" value={f.exit_reason_detail} disabled={!editable} onChange={(e) => set('exit_reason_detail', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Eligible for rehire</Label>
          <NativeSelect className="h-8 text-xs" value={f.rehire_eligible} disabled={!editable} onChange={(e) => set('rehire_eligible', e.target.value)}>
            <option value="">Not set</option>
            <option value="yes">Yes</option>
            <option value="review">Review if they apply</option>
            <option value="no">No</option>
          </NativeSelect>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">Reason{f.rehire_eligible && f.rehire_eligible !== 'yes' ? ' (required, a returning applicant is screened against it)' : ''}</Label>
          <Input className="h-8 text-xs" value={f.rehire_reason} disabled={!editable} onChange={(e) => set('rehire_reason', e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-xs text-foreground sm:col-span-3">
          <input type="checkbox" checked={f.certificate_include_reason === 'yes'} disabled={!editable}
            onChange={(e) => set('certificate_include_reason', e.target.checked ? 'yes' : '')} />
          The employee asked for the reason for termination to be on the Certificate of Service
        </label>
      </div>

      {editable && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="brand" disabled={dirty.length === 0 || save.loading} onClick={() => void submit()}>
            {save.loading && <Spinner className="mr-1 h-3 w-3" />} Save
          </Button>
          {dirty.some((k) => ['notice_received_or_given_date', 'last_working_day', 'employment_end_date', 'notice_position'].includes(k)) && (
            <span className="text-[11px] text-muted-foreground">Saving confirms the dates and redates every open step from them.</span>
          )}
        </div>
      )}
    </section>
  )
}

function LetterPanel({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const item = detail.items.find((i) => i.item_no === 2)
  const preview = useGetOffboardingLetter<{ ok: boolean; to: string | null; subject: string; body_text: string; problems: string[]; sent_at: string | null }>()
  const send = useSendOffboardingLetter<{ ok: boolean }>()
  const [confirm, setConfirm] = useState(false)
  if (!item || item.status === 'not_applicable') return null
  const editable = ['open', 'awaiting_signoff'].includes(detail.summary.status)
  const p = preview.data

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Mail className="h-4 w-4 text-muted-foreground" /> Letter to the employee (step 2)</h3>
      {detail.case.letter_sent_at
        ? <p className="text-xs text-foreground">Sent {formatWhen(detail.case.letter_sent_at)}. {item.evidence}</p>
        : (
          <>
            <Button size="sm" variant="outline" disabled={preview.loading}
              onClick={() => void preview.trigger({ case_id: detail.summary.id }, { skipCache: true }).catch((e) => toast.error(writeErrorMessage(e)))}>
              {preview.loading && <Spinner className="mr-1 h-3 w-3" />} Preview the letter
            </Button>
            {p && (
              <div className="space-y-2 rounded-md border border-dashed border-border p-2 text-xs">
                <p><span className="font-medium">To:</span> {p.to ?? 'no address on file'}</p>
                <p><span className="font-medium">Subject:</span> {p.subject}</p>
                <pre className="whitespace-pre-wrap font-sans text-foreground">{p.body_text}</pre>
                {p.problems.length > 0 && (
                  <ul className="list-disc pl-5 text-rose-700 dark:text-rose-300">{p.problems.map((x) => <li key={x}>{x}</li>)}</ul>
                )}
                {editable && (
                  <Button size="sm" variant="brand" disabled={send.loading || p.problems.length > 0} onClick={() => setConfirm(true)}>Send the letter</Button>
                )}
              </div>
            )}
          </>
        )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this letter to {p?.to}?</AlertDialogTitle>
            <AlertDialogDescription>It is queued at once and cannot be recalled. The case records when it went.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              void send.trigger({ case_id: detail.summary.id }).result
                .then(() => { toast.success('Letter queued'); onChanged() })
                .catch((e) => toast.error(writeErrorMessage(e)))
            }}>Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

type Certificate = {
  ok: boolean; ready: boolean; employee_name: string; id_number: string | null; employer_name: string
  employer_registration_no: string | null; employer_address: string; job_title: string; commenced: string | null
  terminated: string; remuneration: string | null; reason: string | null; problems: string[]
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function CertificatePanel({ caseId }: { caseId: string }) {
  const cert = useGetCertificateOfService<Certificate>()

  async function print() {
    try {
      const c = await cert.trigger({ case_id: caseId }, { skipCache: true })
      if (!c) return
      if (c.problems.length > 0) {
        toast.error(c.problems.join(' '))
        return
      }
      const rows: [string, string][] = [
        ['Employee', c.employee_name + (c.id_number ? ` (ID ${c.id_number})` : '')],
        ['Employer', `${c.employer_name}${c.employer_registration_no ? `, registration ${c.employer_registration_no}` : ''}, ${c.employer_address}`],
        ['Job title', c.job_title],
        ['Employment commenced', formatDay(c.commenced)],
        ['Employment terminated', formatDay(c.terminated)],
        ['Remuneration at termination', c.remuneration ?? 'Not recorded'],
      ]
      if (c.reason) rows.push(['Reason for termination (included at the employee\'s request)', c.reason])
      const w = window.open('', '_blank')
      if (!w) { toast.error('The browser blocked the print window. Allow pop-ups for the cockpit and try again.'); return }
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Certificate of Service</title>
        <style>body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;max-width:640px;margin:48px auto;line-height:1.5}
        h1{font-size:20px;margin-bottom:4px}p.sub{color:#555;margin-top:0}table{border-collapse:collapse;width:100%;margin-top:24px}
        td{border-bottom:1px solid #ddd;padding:8px 4px;vertical-align:top}td:first-child{width:40%;color:#555}
        .sign{margin-top:64px}</style></head><body>
        <h1>Certificate of Service</h1><p class="sub">Issued under section 42 of the Basic Conditions of Employment Act</p>
        <table>${rows.map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`).join('')}</table>
        <p class="sign">Signed for ${escapeHtml(c.employer_name)}: ______________________ Date: ______________</p>
        </body></html>`)
      w.document.close()
      w.focus()
      w.print()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  return (
    <section className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3">
      <Printer className="h-4 w-4 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-foreground">Certificate of Service (step 19)</h3>
        <p className="text-xs text-muted-foreground">Drawn from the case. The reason for termination appears only if the employee asked for it.</p>
      </div>
      <Button size="sm" variant="outline" onClick={() => void print()} disabled={cert.loading}>
        {cert.loading && <Spinner className="mr-1 h-3 w-3" />} Print
      </Button>
    </section>
  )
}

function EmploymentStatePanel({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const rec = useRecordEmploymentState<{ ok: boolean }>()
  const c = detail.case
  if (!['open', 'awaiting_signoff'].includes(detail.summary.status) || !detail.access.can_edit_case) return null
  if (c.driver_status && !['active', 'offboarding'].includes(c.driver_status)) return null
  return (
    <section className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-3">
      <UserMinus className="h-4 w-4 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-foreground">Confirm the exit</h3>
        <p className="text-xs text-muted-foreground">The driver is held as Offboarding, out of the Active list. Once the last working day has passed, confirm the exit here: both the application and the driver record move to resigned or dismissed.</p>
      </div>
      <Button size="sm" variant="outline" disabled={rec.loading}
        onClick={() => void rec.trigger({ case_id: detail.summary.id }).result.then(() => { toast.success('Employment state recorded'); onChanged() }).catch((e) => toast.error(writeErrorMessage(e)))}>
        Confirm the exit
      </Button>
    </section>
  )
}

function NotesPanel({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const add = useAddOffboardingNote<{ ok: boolean }>()
  const [body, setBody] = useState('')
  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <h3 className="text-sm font-semibold text-foreground">Notes</h3>
      <div className="flex gap-2">
        <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={detail.summary.status === 'closed' ? 'A correction after sign off is a dated note here.' : 'Add a note to the case'} />
        <Button size="sm" variant="outline" className="self-end" disabled={!body.trim() || add.loading}
          onClick={() => void add.trigger({ case_id: detail.summary.id, body }).result.then(() => { setBody(''); onChanged() }).catch((e) => toast.error(writeErrorMessage(e)))}>
          Add
        </Button>
      </div>
      <ul className="space-y-1.5">
        {detail.notes.map((n, i) => (
          <li key={i} className="text-xs">
            <span className="text-muted-foreground">{formatWhen(n.at)} · {n.author}: </span>
            <span className="text-foreground">{n.body}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function CancelPanel({ detail, onChanged }: { detail: Detail; onChanged: () => void }) {
  const cancel = useCancelOffboarding<{ ok: boolean }>()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  if (!['open', 'awaiting_signoff'].includes(detail.summary.status)) return null
  if (!(detail.access.can_sign_off || detail.access.can_approve)) return null
  return (
    <section className="pb-6">
      {!open ? (
        <button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)}>
          <X className="h-3 w-3" /> Cancel this case
        </button>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <Input className="h-8 max-w-md text-xs" placeholder="Why it is being cancelled" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" variant="destructive" disabled={!reason.trim() || cancel.loading}
            onClick={() => void cancel.trigger({ case_id: detail.summary.id, reason }).result.then(() => { toast.success('Case cancelled'); setOpen(false); onChanged() }).catch((e) => toast.error(writeErrorMessage(e)))}>
            Cancel the case
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Keep it</Button>
        </div>
      )}
    </section>
  )
}
